import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import useHelpers from 'hooks/helpers';
import React, { useMemo } from 'react';
import styles from './ArticleCard.module.scss';
import Readtime from 'components/includes/Readtime';
import { nameToSlug } from 'model/Article';

export type ArticleFields = {
  articleImage?: string;
  articleStrapline: {
    value: string;
  };
  articleTitle: {
    value: string;
  };
  articleType: string;
  baseUrl?: string;
  category: string;
  name: string;
  publicationDate: string;
  url?: string;
  duration?: {
    value: number;
  };
};

type Props = {
  fields: ArticleFields;
  useLineClamps?: boolean;
  cardCategoryField?: boolean | string;
};

const ArticleCard = ({ fields, cardCategoryField, useLineClamps = true }: Props) => {
  const { t } = useI18n();
  const { localizeDate } = useHelpers();
  const baseUrl = fields.baseUrl;

  const getCategoryText = useMemo(() => {
    if (cardCategoryField) return fields[cardCategoryField];
    return fields.category;
  }, [cardCategoryField, fields]);

  const url = useMemo(() => {
    if (!fields?.url) {
      return baseUrl ? `${baseUrl}/${nameToSlug(fields?.name)}` : '/';
    }
    return fields.url;
  }, [fields, baseUrl]);

  const isTechzoneArticle: boolean = !!fields.url?.includes('https://techzone');

  const scLinkField = useMemo(() => {
    let value;

    if (isTechzoneArticle) {
      value = {
        text: t('readOnTechzone'),
        target: '_blank',
        href: fields.url,
      };
    } else {
      value = {
        href: url,
      };
    }

    return { value };
  }, [isTechzoneArticle, t, url, fields.url]);

  return (
    <ScLink
      field={scLinkField}
      showExternalIcon={false}
      data-e2e-component-uid="renderingId"
      className={`${styles['article-card']} hover-text-decoration-none`}
    >
      <div className="d-flex flex-column h-100">
        <div className="flex-shrink-0 position-relative">
          <NextImage
            field={{ src: fields.articleImage }}
            alt={fields.articleTitle.value}
            className={styles['article-card__thumbnail']}
            width={0}
            height={0}
            sizes="
              (max-width: 767px) 100vw,
              (max-width: 991px) 50vw,
              25vw
            "
          />
          {isTechzoneArticle && (
            <div
              className={`${styles['article-card__badge']} text-link background-digital-light-grey`}
            >
              {t('techzone')}
            </div>
          )}
        </div>
        <div
          className={`${styles['article-card__body']} py-3 px-2 flex-grow-1 background-digital-light-grey`}
        >
          <div className="d-flex align-items-start section-header-small">
            <span className="article-category section-header-small">{getCategoryText}</span>
            <time className="ms-auto section-header-small text-nowrap">
              {localizeDate(fields.publicationDate)}
            </time>
          </div>
          <div className={`mt-2 ${useLineClamps ? 'mb-8' : 'mb-2'}`}>
            <h5
              className={`${styles['article-card__title']} ${useLineClamps ? 'line-clamp-4' : ''}`}
            >
              {fields.articleTitle.value}
            </h5>
            <p className={`body-small text-grey mt-1 ${useLineClamps ? 'line-clamp-3' : ''}`}>
              {fields.articleStrapline.value}
            </p>
          </div>
          <div className="mt-auto">
            {isTechzoneArticle ? (
              <span className="text-link">
                <span>{scLinkField.value.text}</span>
                <i className="bi bi-box-arrow-up-right ms-1" />
              </span>
            ) : (
              <Readtime
                articleType={fields.articleType}
                duration={Number(fields.duration?.value ?? '')}
                className="section-header-small"
              />
            )}
          </div>
        </div>
      </div>
    </ScLink>
  );
};

export default ArticleCard;
